
import React, {useRef} from "react";
import "./styles/global.scss";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import Routers from "./routes/routes";

import { getAuthInstance, setOriginalUri, renewToken } from "./utils/auth/unified-auth";
import { AUTH_PROVIDER, isOkta, isMsal } from "./utils/auth/auth-config";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { MsalProvider } from "@azure/msal-react";
import { ThemeProvider } from 'pepsico-ds'
import WidgetPanel from "./components/Button/WidgetPanel"
import "pepsico-ds/css"

import "leaflet/dist/leaflet.css";
import { useEffect, useState } from 'react';
import AuthLoginTimeTracker from "./components/LoginTracker/AuthLoginTimeTracker";

import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastProvider } from './components/Toast/ToastContext';
import AuthTokenHandler from "../src/components/AuthTokenHandler/AuthTokenHandler";
// import { useIdleTimer } from 'react-idle-timer';


const App = () => {
  const [authInstance, setAuthInstance] = React.useState(null);
  
  React.useEffect(() => {
    getAuthInstance().then(instance => {
      setAuthInstance(instance);
      console.log(`${AUTH_PROVIDER} auth instance initialized`, instance);
    });
  }, []);

  const [enableNPSWidget, setEnableNPSWidget] = useState(false)
  const [flag, setFlag] = useState(false)

  // npm install react-idle-timer
  // const handleOnIdle = () => {
  //   console.warn("User is idle for 3 minutes. Logging out...");
  //   localStorage.removeItem("ph-login-time");
  //   localStorage.removeItem("ph-login-time-display");
  //   localStorage.removeItem("ph-alert-shown");
  //   oktaAuth.signOut(); // Or oktaLogout(oktaAuth) if you have a custom util
  // };

  // useIdleTimer({
  //   timeout: 45 * 60 * 1000, // 45 minutes
  //   onIdle: handleOnIdle,
  //   debounce: 500,
  // });


  /*** OLD LOGIC ***/
  // useEffect(() => {
  //   // Subscribe to token expiration events
  //   const tokenManager = oktaAuth.tokenManager;

  //   const handleTokenExpired = async (key) => {
  //     console.log(`Token with key ${key} has expired.`);
  //     await oktaAuth.signInWithRedirect();
  //   };

  //   tokenManager.on('expired', handleTokenExpired);

  //   return () => {
  //     tokenManager.off('expired', handleTokenExpired);
  //   };
  // }, [])

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const loginTime = parseInt(localStorage.getItem("ph-login-time"), 10);
  //     const alertShown = localStorage.getItem("ph-alert-shown");
  //     if (!isNaN(loginTime) && alertShown !== "true") {
  //       const currentTime = Date.now();
  //       const elapsed = currentTime - loginTime;
  //       const delay = 5 * 60 * 1000; // 5 minutes
  //       // console.log("checking...", elapsed)
  //       if (elapsed >= delay) {
  //         // alert("10 minutes have passed since login.");
  //         setEnableNPSWidget(true);
  //         localStorage.setItem("ph-alert-shown", "true");
  //         clearInterval(interval); // stop checking after alert
  //       }
  //     }
  //   }, 1000); // check every 5 seconds
  //   return () => clearInterval(interval);
  // }, []);

  const periodicRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const loginTime = parseInt(localStorage.getItem("ph-login-time"), 10);
    const alertShown = localStorage.getItem("ph-alert-shown") === "true";

    // Nothing to do if loginTime is invalid
    if (isNaN(loginTime)) return;

    const FIVE_MIN = 5 * 60 * 1000;
    const SEVEN_MIN = 7 * 60 * 1000;
    const now = Date.now();

    // Helper to start the repeating 7-minute interval
    const startPeriodic = () => {
      // Ensure only one interval exists
      if (periodicRef.current) return;
      periodicRef.current = setInterval(() => {
        setEnableNPSWidget(true);
        setFlag(prev => !prev);
      }, SEVEN_MIN);
    };

    // If alert already shown, we still start periodic repeats immediately
    if (alertShown) {
      startPeriodic();
      return () => {
        if (periodicRef.current) clearInterval(periodicRef.current);
      };
    }

    // If 5 minutes have already passed, fire once and start periodic
    const elapsed = now - loginTime;
    if (elapsed >= FIVE_MIN) {      
      setEnableNPSWidget(true);
      localStorage.setItem("ph-alert-shown", "true");
      startPeriodic();
    } else {
      // Schedule a single timeout for the remaining time until the 5-minute mark
      const remaining = FIVE_MIN - elapsed;
      timeoutRef.current = setTimeout(() => {
        setEnableNPSWidget(true);
        localStorage.setItem("ph-alert-shown", "true");

        // after initial trigger, start periodic 7-min interval
        startPeriodic();
        timeoutRef.current = null;
      }, remaining);
    }

    // cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (periodicRef.current) {
        clearInterval(periodicRef.current);
        periodicRef.current = null;
      }
    };
  }, []); // run once on mount

  useEffect(() => {
    if (!authInstance) return;
    
    if (isOkta()) {
      const tokenManager = authInstance.tokenManager;
      
      tokenManager.on('expired', async (key) => {
        console.log(`Token expired: ${key}`);
        try {
          const renewedToken = await tokenManager.renew(key);
          console.log(`Token renewed: ${renewedToken}`);
        } catch (error) {
          console.error("Error renewing token:", error);
          const currentUri = window.location.pathname + window.location.search + window.location.hash;
          await setOriginalUri(currentUri);
          authInstance.signInWithRedirect();
        }
      });
      
      return () => {
        tokenManager.off('expired');
      };
    }
    // MSAL handles token renewal automatically
  }, [authInstance]);

  // Older Logic
  // oktaAuth.tokenManager.on('expired', (key, expiredToken) => {
  //   console.log(`Token expired: ${key}`);
  //   oktaLogout(oktaAuth)
  // });

  const customAuthHandler = async () => {
    if (!authInstance) return;
    
    if (isOkta()) {
      const previousAuthState = authInstance.authStateManager.getPreviousAuthState();
      if (!previousAuthState || !previousAuthState.isAuthenticated) {
        const currentUri = window.location.pathname + window.location.search + window.location.hash;
        console.log("setting Original URL:", currentUri);
        await setOriginalUri(currentUri);
        await authInstance.signInWithRedirect();
      }
    } else if (isMsal()) {
      // MSAL handles this via MsalProvider
      const { msalLogin } = await import('./utils/msal/msal-auth');
      await msalLogin();
    }
  };

  /*** OLD LOGIC 
  const restoreOriginalUri = async () => {
    window.location.replace(toRelativeUrl("/", window.location.origin));
  };
  ***/
  const restoreOriginalUri = async (authInst, originalUri) => {
    if (isOkta()) {
      const relative = toRelativeUrl(originalUri || '/', window.location.origin);
      console.log("Original Uri to be restored:", relative);
      window.location.replace(relative);
    } else if (isMsal()) {
      const savedUri = sessionStorage.getItem('msal.original.uri') || originalUri || '/';
      sessionStorage.removeItem('msal.original.uri');
      window.location.replace(savedUri);
    }
  };

  /*** OLD LOGIC 
  const triggerLogin = async () => {
    oktaLogin(oktaAuth);
  };
  ***/
  return (
    // <div className="app-container">
    <div style={{height:"100%"}}
    // className="scaled-div" 
    // style={{
    //     transform: 'scale(0.8)',
    //     transformOrigin: 'top left',
    //     width: '125vw', // 100 / 0.8 = 125%
    //     height: '125vh',
    // }}
    >
       <ToastContainer
                        position="bottom-center"
                        autoClose={5000}
                        toastClassName="custom-toast"
                        bodyClassName="custom-toast-body"
                        // style={{
                          // position: 'relative',
                          // right: '10rem',
                          // display: 'flex !important',
                          // justifyContent: 'center !important',
                          // alignItems: 'center !important'
                        // }}
                        transition={Slide}
                      //   className="toast-container"
            />
      <ToastProvider>
        <Provider store={store}>
          {isOkta() && authInstance ? (
            <Security
              oktaAuth={authInstance}
              onAuthRequired={customAuthHandler}
              restoreOriginalUri={restoreOriginalUri}
            >
              <AuthLoginTimeTracker/>
              <div className="scaled-div">
                <Routers />
              </div>
              <WidgetPanel enablePostTimer={enableNPSWidget} flag={flag}/>
            </Security>
          ) : isMsal() && authInstance ? (
            <MsalProvider instance={authInstance}>
              <AuthLoginTimeTracker/>
              <div className="scaled-div">
                <Routers />
              </div>
              <WidgetPanel enablePostTimer={enableNPSWidget} flag={flag}/>
            </MsalProvider>
          ) : null}
        </Provider>
        </ToastProvider>
    </div>
    // </div>
  );
};

export default App;
